import type { ProductPlan } from '@/features/products/types';
import { cn } from '@/lib/utils';
import { formatMoney } from '@/shared/lib/money';
import {
  displayPrice,
  displayStrikePrice,
  isMonthlyPlan,
  planPeriodHeading,
} from '../domain/pricing';
import checkIcon from '../assets/icon-plan-check.svg';

interface PlanCardV2Props {
  plan: ProductPlan;
  selected: boolean;
  onSelect: () => void;
  index: number;
  total: number;
}

const POPULAR_INDEX = 1;
const TRIAL_MONTH_PLAN_ID = 'moneyfox_3d499_1m4999';

function v2Badges(index: number, total: number): Array<'popular' | 'value'> {
  const badges: Array<'popular' | 'value'> = [];
  if (total >= 2 && index === POPULAR_INDEX) badges.push('popular');
  if (total >= 2 && index === total - 1) badges.push('value');
  return badges;
}

function v2Features(index: number, total: number): string[] {
  if (index === 0) return ['Flexible month-to-month'];
  if (total > 1 && index === total - 1) return ['Best price per month', 'Longest savings period'];
  if (index === POPULAR_INDEX) return ['Great balance of commitment & savings'];
  return [];
}

export function PlanCardV2({ plan, selected, onSelect, index, total }: PlanCardV2Props) {
  const isTrialMonth = plan.id === TRIAL_MONTH_PLAN_ID;
  const strike = displayStrikePrice(plan);
  const unitPrice = displayPrice(plan);
  const periodSuffix = isTrialMonth ? '/trial' : isMonthlyPlan(plan) ? '/mo' : null;
  const strikeSuffix = isTrialMonth ? '/mo' : null;
  const heading = isTrialMonth ? '1 MONTH - TRIAL' : planPeriodHeading(plan);
  const badges = v2Badges(index, total);
  const features = v2Features(index, total);
  const hasBadge = badges.length > 0;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'relative flex w-full flex-col items-start rounded-2xl bg-white text-left transition-colors',
        hasBadge ? 'gap-1 pb-4 pl-4 pr-3 pt-3' : 'gap-2 px-4 py-4 pr-3',
        selected
          ? 'border-[1.5px] border-mf-green'
          : 'border-[1.5px] border-mf-neutral-200',
      )}
      style={
        selected
          ? {
              backgroundImage:
                'linear-gradient(249.36deg, rgba(17, 170, 89, 0.05) 0%, rgba(107, 206, 62, 0.05) 100%)',
            }
          : undefined
      }
    >
      <span className="flex w-full items-center justify-between">
        <span className="text-xs leading-4 text-mf-neutral-900">{heading}</span>
        {hasBadge ? (
          <span className="flex shrink-0 items-center gap-1">
            {badges.map((badge) => (
              <span
                key={badge}
                className={cn(
                  'rounded-full px-3 py-1 text-xs font-bold leading-4 text-white',
                  badge === 'popular' ? 'bg-mf-green' : 'bg-[#844fe1]',
                )}
              >
                {badge === 'popular' ? 'MOST POPULAR' : 'BEST VALUE'}
              </span>
            ))}
          </span>
        ) : null}
      </span>

      <span className="flex flex-col gap-3">
        <span className="flex flex-col">
          <span className="flex items-center gap-1">
            <span className="font-display text-[28px] font-bold leading-9 text-mf-neutral-900">
              {formatMoney(unitPrice, plan.currency)}
            </span>
            {periodSuffix ? (
              <span className="pt-2 text-[15px] leading-5 text-mf-neutral-600">{periodSuffix}</span>
            ) : null}
            {strike != null ? (
              <span className="flex items-center pt-2">
                <span className="relative text-[15px] leading-5 text-mf-neutral-400">
                  {formatMoney(strike, plan.currency)}
                  <span className="absolute inset-x-0 top-1/2 h-px bg-mf-error" />
                </span>
                {strikeSuffix ? (
                  <span className="text-[15px] leading-5 text-mf-neutral-400">{strikeSuffix}</span>
                ) : null}
              </span>
            ) : null}
          </span>
          <span className="text-sm leading-4 text-mf-neutral-400">
            {formatMoney(plan.price, plan.currency)} billed now
          </span>
        </span>

        {features.length > 0 ? (
          <span className="flex flex-col gap-1">
            {features.map((feature) => (
              <span key={feature} className="flex items-center gap-1">
                <span className="flex size-4 shrink-0 items-center justify-center overflow-clip">
                  <img src={checkIcon} alt="" className="size-3" />
                </span>
                <span className="text-sm leading-4 text-mf-neutral-600">{feature}</span>
              </span>
            ))}
          </span>
        ) : null}
      </span>
    </button>
  );
}
